import React, { useState, useContext, useCallback } from 'react'
import injectStyles from 'react-jss'
import cn from 'classnames'

import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import OutlinedInput from '@material-ui/core/OutlinedInput'
import InputAdornment from '@material-ui/core/InputAdornment'
import FormHelperText from '@material-ui/core/FormHelperText'

import { ButtonInline } from 'Components/Buttons/ButtonInline'
import { SnackBarContext } from 'Components/SnackBar'

import EditIcon from 'Assets/Images/icons/edit'
import DeleteIcon from 'Assets/Images/icons/cancel'
import SaveIcon from 'Assets/Images/icons/save'
import { theme } from 'Assets/Styles/theme'

import styles from './styles.js'
import { useHandleEdit } from '../../../../useHandleEdit.js'
import { WEEKLY_MISSION_LIMIT } from '../../../../../../constants.js'

const isValueInRange = value => value >= 0 && value <= 100

const CountrySettingsTable = ({
  classes,
  title = '',
  currencySign = '$',
  countryDefaultSettings = {},
  countrySettings = {},
  setCountrySettings,
  options = [],
  onSave = () => {},
  setNewCountrySettings = () => {},
  isEditingTable = false,
  setIsEditingTable = () => {},
}) => {
  // Only complain about a field this table actually shows: the BA payout error
  // used to be raised by every table, which left sections like "Default
  // campaign limits" with no Save button at all.
  const sectionKeys = options.map(option => option.key)
  const defaultErrors =
    sectionKeys.includes('baPayout') && !countryDefaultSettings.baPayout
      ? [
          {
            key: 'baPayout',
            value:
              'The BA payout per mission value should always be greater than 0.',
          },
        ]
      : []
  const [errorsList, setErrorsList] = useState(defaultErrors)

  const runSnackBar = useContext(SnackBarContext)

  const { isEdit, handleEditMode } = useHandleEdit({
    isEditingTable,
    setIsEditingTable,
  })

  const renderValue = (key, type, value) => {
    if (key === WEEKLY_MISSION_LIMIT && value === null) return 'Unlimited'
    switch (type) {
      case 'currency':
        return `${currencySign}${value}`
      case 'number':
        return value
      case 'percent':
        return `${value}%`

      default:
        return value
    }
  }

  const isValueValid = value => {
    if (!value) {
      return true
    }
    if (Number(value) < 0) {
      return false
    }
    return !/^\s*$/.test(value) && !isNaN(value)
  }

  const validateAddidionalData = (key, value) => {
    switch (key) {
      case 'baPerLocation':
        if (value < 1) {
          setErrorsList([
            ...errorsList,
            {
              key: key,
              value: 'BAs per H2H location should be greater than 1.',
            },
          ])
        } else {
          setErrorsList(errorsList.filter(error => error.key !== key))
        }
        break
      case 'flyerPackH2h':
      case 'flyerPackD2d':
        // Blank or 0 means the pack is not set for this country, which is
        // allowed - the campaign or city value is used instead.
        if (!value) {
          setErrorsList(errorsList.filter(error => error.key !== key))
        } else if (value < 100) {
          setErrorsList([
            ...errorsList,
            { key: key, value: 'Flyer pack should be greater than 100.' },
          ])
        } else {
          setErrorsList(errorsList.filter(error => error.key !== key))
        }
        break
      case 'baPayout':
        if (!value || value === '00') {
          setErrorsList([
            ...errorsList,
            {
              key: key,
              value:
                'The BA payout per mission value should always be greater than 0.',
            },
          ])
        } else {
          setErrorsList(errorsList.filter(error => error.key !== key))
        }
        break
      case 'defaultCampaignLimits':
        if (value === '00' || !value || value > 30 || value < 1) {
          setErrorsList([
            ...errorsList,
            {
              key: key,
              value: 'This value must be between 1 and 30 days.',
            },
          ])
        } else {
          setErrorsList(errorsList.filter(error => error.key !== key))
        }
        break

      default:
        break
    }
  }

  const renderOutlineInput = ({ key, type }) => {
    switch (type) {
      case 'currency':
        return (
          <OutlinedInput
            id={key}
            value={countrySettings[key]}
            onChange={event => {
              const { value } = event.target
              validateAddidionalData(key, value)

              return isValueValid(value)
                ? setCountrySettings({
                    ...countrySettings,
                    [key]: value
                      ? value[value.length - 1] === '.'
                        ? value
                        : Number(value)
                      : 0,
                  })
                : null
            }}
            startAdornment={
              <InputAdornment position="start">{currencySign}</InputAdornment>
            }
            classes={{
              input: classes.outlinedLeftInput,
            }}
          />
        )
      case 'number':
        return (
          <OutlinedInput
            id={key}
            value={countrySettings[key]}
            onChange={event => {
              const { value } = event.target
              let newValue = value
                ? Number(value.toString().replace(/[^\d]/g, ''))
                : value

              validateAddidionalData(key, newValue)

              return isValueValid(newValue)
                ? setCountrySettings({
                    ...countrySettings,
                    [key]: newValue
                      ? Number(newValue)
                      : key === WEEKLY_MISSION_LIMIT && newValue === ''
                      ? null
                      : 0,
                  })
                : null
            }}
            classes={{
              input: cn(classes.outlinedLeftInput, classes.textAlignEnd),
            }}
          />
        )

      case 'percent':
        return (
          <OutlinedInput
            id={key}
            value={countrySettings[key]}
            onChange={event => {
              const { value } = event.target
              return isValueValid(value) && isValueInRange(value)
                ? setCountrySettings({
                    ...countrySettings,
                    [key]: value
                      ? value[value.length - 1] === '.'
                        ? value
                        : Number(value)
                      : 0,
                  })
                : null
            }}
            endAdornment={<InputAdornment position="end">%</InputAdornment>}
            classes={{
              input: cn(classes.outlinedRightInput, classes.textAlignEnd),
            }}
          />
        )

      default:
        return null
    }
  }

  const handleSave = useCallback(async () => {
    const response = await onSave()
    if (!response) return
    const { success, error } = response
    if (success) {
      runSnackBar({
        type: 'success',
        msg: `${title} were saved successfully`,
      })
      const correctCountrySettings = Object.entries(countrySettings).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: key === WEEKLY_MISSION_LIMIT && !value ? value : Number(value),
        }),
        {},
      )
      setCountrySettings(correctCountrySettings)
      setNewCountrySettings(correctCountrySettings)
      handleEditMode(false)
    } else {
      runSnackBar({
        type: 'error',
        msg: `There is an error while saving ${title}.`,
        error: error,
      })
    }
  }, [countrySettings])

  const handleCansel = useCallback(() => {
    setErrorsList(defaultErrors)
    setCountrySettings(countryDefaultSettings)
    handleEditMode(false)
  }, [countryDefaultSettings])

  const renderError = key => {
    const error = errorsList.find(error => error.key === key) || {}

    return Object.keys(error) ? (
      <FormHelperText error id={`${key}-error`}>
        {error.value}
      </FormHelperText>
    ) : null
  }

  return (
    <Box
      className={classes.wrapper}
      key={title
        .toLocaleLowerCase()
        .split(' ')
        .join('-')}
    >
      <Box className={classes.header}>
        <Typography className={classes.title}>{title}</Typography>
        <Box>
          {isEdit ? (
            <Box className={classes.btnWrapper}>
              <ButtonInline
                label={'Save'}
                labelColor={theme.colors.primary}
                icon={<SaveIcon />}
                onClickHandler={() =>
                  errorsList.length
                    ? runSnackBar({
                        type: 'error',
                        msg: errorsList.map(error => error.value).join(' '),
                      })
                    : handleSave()
                }
              />
              <ButtonInline
                label={'Cancel'}
                icon={<DeleteIcon />}
                onClickHandler={() => handleCansel()}
              />
            </Box>
          ) : (
            <ButtonInline
              label={'Edit'}
              icon={<EditIcon style={{ marginLeft: '7px' }} />}
              labelFirst
              onClickHandler={() => handleEditMode(true)}
            />
          )}
        </Box>
      </Box>
      <Box className={classes.tableItemsWrapper}>
        {options.map(option => (
          <Box key={option.key} className={classes.tableItem}>
            <Box className={classes.text}>
              <Typography className={classes.label}>{option.label}</Typography>
              <Typography className={classes.description}>
                {option.description}
              </Typography>
            </Box>
            <Box className={classes.value}>
              {isEdit ? (
                renderOutlineInput(option)
              ) : (
                <Typography>
                  {renderValue(
                    option.key,
                    option.type,
                    countrySettings[option.key],
                  )}
                </Typography>
              )}
            </Box>
            {renderError(option.key)}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default injectStyles(styles)(CountrySettingsTable)
